import type { ResultadoMotivo, ResultadoTipo, TorneoEstado } from '@domino/schema'

/**
 * Los estados, dichos como los diría el jugador.
 *
 * No repiten los textos de la app de escritorio, y esa diferencia es a
 * propósito. Al organizador «Inscripción» le dice en qué etapa está el torneo
 * que administra; al jugador lo que le importa de ese mismo estado es que él
 * está anotado y todavía no se juega. Mismo dato, otra pregunta.
 */

const TORNEO: Record<TorneoEstado, { clase: string; texto: string }> = {
  borrador: { clase: 'neutra', texto: 'En preparación' },
  inscripcion: { clase: 'info', texto: 'Anotado' },
  en_curso: { clase: 'vivo', texto: 'Jugándose ahora' },
  finalizado: { clase: 'cerrado', texto: 'Terminado' },
  cancelado: { clase: 'error', texto: 'Cancelado' },
}

export function PastillaTorneo({ estado }: { estado: TorneoEstado }) {
  const e = TORNEO[estado] ?? { clase: 'neutra', texto: estado }
  return <span className={`pastilla ${e.clase}`}>{e.texto}</span>
}

/**
 * El estado de un torneo en la vitrina, para quien no juega.
 *
 * «Anotado» no le dice nada al que mira desde afuera: para él la inscripción es
 * que el torneo todavía no empezó y que se puede anotar. Y el que se juega lleva
 * el punto rojo de las transmisiones, que se entiende sin leer.
 */
export function PastillaVitrina({ estado }: { estado: TorneoEstado }) {
  if (estado === 'en_curso') {
    return (
      <span className="pastilla en-vivo">
        <span className="punto-vivo" aria-hidden="true" />
        En vivo
      </span>
    )
  }
  if (estado === 'inscripcion') return <span className="pastilla info">Inscripción abierta</span>
  return <PastillaTorneo estado={estado} />
}

/**
 * Cómo terminó una partida suya.
 *
 * El bye lleva pastilla propia y no la de «Ganada» aunque acredite puntos: no
 * la ganó nadie, no se jugó, y el que la recibe necesita que la pantalla se lo
 * diga con todas las letras. Es la duda que más llega a la mesa de control.
 */
export function PastillaResultado({
  resultado,
  motivo,
}: {
  resultado: ResultadoTipo
  motivo: ResultadoMotivo
}) {
  if (motivo === 'bye' || motivo === 'mesa_incompleta') {
    return <span className="pastilla info">Mesa incompleta</span>
  }
  if (motivo === 'ausencia') return <span className="pastilla error">No se presentó</span>
  if (motivo === 'abandono') return <span className="pastilla error">Abandono</span>

  return resultado === 'ganada' ? (
    <span className="pastilla ok">Ganada</span>
  ) : (
    <span className="pastilla perdida">Perdida</span>
  )
}
